/*
 * h4h-card-swipe.js — the touch half of the product card's second image.
 *
 * WHAT WAS MEASURED, before any of this was written. On the rendered preview at
 * 390 px, every product card holds exactly TWO images. The first computes
 * `opacity: 1`, the second `opacity: 0`, there is no Swiper on the card, no
 * pagination dots, `touch-action: auto` and `overflow: visible`. The only rule
 * that ever reveals the second image is
 *
 *     @media screen and (min-width: 1025px) { .card__media:hover ... }
 *
 * in `component-card.css`. So on a phone the second photograph is downloaded,
 * decoded, laid out — and unreachable. Desktop customers get a second view of
 * the product; phone customers, who are the majority on this store, get none,
 * and pay for the bytes anyway. D-226 closed the hover half as correct; this is
 * the half it left open.
 *
 * WHY A SWIPE AND NOT A CAROUSEL. There are exactly two images, and the pair is
 * already in the DOM with a working opacity transition. Adding a Swiper would
 * mean new markup, a second lazily-initialising instance per card (D-221 records
 * how badly that behaves here), and a much larger change than the problem needs.
 * This toggles one class and reuses the transition the vendor already ships.
 *
 * DISCOVERABILITY. A gesture nobody knows about is not a feature, so cards that
 * have a real second image get two small dots. The dots are also the state
 * readout, and they are clickable, which gives the same affordance to anyone who
 * cannot make a swipe gesture.
 *
 * THE THREE THINGS THAT WOULD BREAK IF THIS WERE NAIVE, all handled below:
 *  1. The card is wrapped in a link. A swipe must not navigate. The click that
 *     follows a gesture is swallowed once, in the capture phase.
 *  2. Vertical page scrolling must keep working. The gesture is only claimed
 *     once horizontal travel exceeds vertical travel AND the threshold.
 *  3. `h4h-swatch-pilot.js` rewrites BOTH images' `src` when a colour is chosen.
 *     That is compatible by construction: this file never touches `src`, only
 *     which of the two is shown, so a swiped card that changes colour keeps
 *     showing the second photograph — now of the new colour.
 *
 * Delegated from `document`, so cards added later by filtering or pagination are
 * covered without a MutationObserver.
 */
(function () {
  'use strict';

  var HOVER_BREAKPOINT = '(min-width: 1025px)';
  var THRESHOLD = 30; // px of horizontal travel before a drag counts as a swipe
  var SWIPED = 'h4h-card-swiped';
  var READY = 'h4h-swipe-ready';

  // Above the hover breakpoint the CSS already does this, and adding a second
  // mechanism there would fight it.
  function touchMode() {
    return !window.matchMedia(HOVER_BREAKPOINT).matches;
  }

  function mediaOf(card) {
    return card.querySelector('.media--hover-effect');
  }

  /* A card qualifies only when it really has two images to show. Cards with one
     photograph must get no dots and no gesture — an affordance that does nothing
     is worse than none. */
  function imagesOf(card) {
    var m = mediaOf(card);
    if (!m) return [];
    return Array.prototype.filter.call(m.children, function (el) {
      return el.tagName === 'IMG';
    });
  }

  function addDots(card) {
    if (card.classList.contains(READY)) return;
    card.classList.add(READY);

    var nav = document.createElement('div');
    nav.className = 'h4h-card-dots';
    nav.setAttribute('aria-hidden', 'true'); // the link text already names the product
    for (var i = 0; i < 2; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'h4h-card-dot' + (i === 0 ? ' is-active' : '');
      b.setAttribute('tabindex', '-1');
      b.dataset.h4hDot = String(i);
      nav.appendChild(b);
    }
    var media = card.querySelector('.card__media') || card;
    media.appendChild(nav);
  }

  function setState(card, swiped) {
    var media = card.querySelector('.card__media') || card;
    media.classList.toggle(SWIPED, swiped);
    var dots = card.querySelectorAll('.h4h-card-dot');
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', (i === 1) === swiped);
    }
  }

  function isSwiped(card) {
    var media = card.querySelector('.card__media') || card;
    return media.classList.contains(SWIPED);
  }

  /* ---- gesture ---------------------------------------------------------- */

  var start = null;
  var moved = false;
  var activeCard = null;

  function cardFrom(target) {
    return target && target.closest ? target.closest('.card-wrapper') : null;
  }

  document.addEventListener(
    'pointerdown',
    function (e) {
      if (!touchMode() || e.pointerType === 'mouse') return;
      var card = cardFrom(e.target);
      if (!card || imagesOf(card).length < 2) return;
      activeCard = card;
      start = { x: e.clientX, y: e.clientY };
      moved = false;
    },
    { passive: true }
  );

  document.addEventListener(
    'pointermove',
    function (e) {
      if (!start || !activeCard) return;
      var dx = e.clientX - start.x;
      var dy = e.clientY - start.y;
      // Claim the gesture only when it is clearly horizontal, so the page can
      // still be scrolled by dragging over a card.
      if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        moved = true;
        setState(activeCard, dx < 0); // swipe left -> second image, right -> back
      }
    },
    { passive: true }
  );

  function endGesture() {
    start = null;
    activeCard = null;
  }

  document.addEventListener('pointerup', endGesture, { passive: true });
  document.addEventListener('pointercancel', endGesture, { passive: true });

  /* The click that Chrome fires after a drag would follow the card link. Swallow
     exactly one, in the capture phase, then forget it. */
  document.addEventListener(
    'click',
    function (e) {
      if (!moved) return;
      moved = false;
      if (!cardFrom(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    },
    true
  );

  /* Dots are a real control, not decoration. */
  document.addEventListener(
    'click',
    function (e) {
      var dot = e.target.closest ? e.target.closest('.h4h-card-dot') : null;
      if (!dot) return;
      e.preventDefault();
      e.stopPropagation();
      var card = cardFrom(dot);
      if (card) setState(card, dot.dataset.h4hDot === '1');
    },
    true
  );

  /* ---- setup ------------------------------------------------------------ */

  function scan() {
    if (!touchMode()) return;
    var cards = document.querySelectorAll('.card-wrapper');
    for (var i = 0; i < cards.length; i++) {
      if (imagesOf(cards[i]).length >= 2) addDots(cards[i]);
    }
  }

  /* Images arrive lazily and grids are replaced wholesale by filtering, so rescan
     on a debounced observer — one pass per frame, the same shape D-224 settled on
     for the swatch pilot, because this also watches the whole document. */
  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      scan();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  window.addEventListener('resize', schedule, { passive: true });
})();
